import { useMutation } from "react-query";

import axios, { AxiosResponse } from "axios";
import { RigFracChartRequestType } from "types";
import { ExportFormat } from "utils/exportUtils";
import * as z from "zod";

const chartServiceApiRoot = process.env.REACT_APP_CHART_SERVICE;

export const ExportRigFracActivityResponseSchema = z.object({
  data: z.instanceof(Blob),
  status: z.number().min(200).max(299)
});

export type ExportRigFracActivityResponse = z.infer<
  typeof ExportRigFracActivityResponseSchema
>;

export interface ExportRigFracActivityParams {
  request: RigFracChartRequestType;
  format: ExportFormat;
  filename: string;
}

/**
 * Export mutation for the Rig/Frac Activity chart (EVA-76 US4). Mirrors
 * useExportChartDataMutation but POSTs the LEAN rig/frac request to the
 * dedicated rigFracChart export route (NOT the wells /chart/export).
 */
export default function useExportRigFracActivityMutation(
  onSuccess?: (response: AxiosResponse, params: ExportRigFracActivityParams) => void,
  onError?: (error?: Error) => void
) {
  return useMutation(
    async ({ request, format }: ExportRigFracActivityParams) => {
      const response = await axios.post(
        // chartServiceApiRoot already ends in /api/v1.
        `${chartServiceApiRoot}/rigFracChart/export?exportformat=${format}`,
        request,
        {
          responseType: "blob"
        }
      );

      // A no-data export answers HTTP 200 with a JSON ChartResult body (the
      // widget-state convention), which responseType:"blob" still wraps in a
      // Blob — so a Blob+2xx check alone would save the JSON as a spreadsheet.
      // Only a csv/xlsx content-type is a real file.
      const contentType = String(response.headers?.["content-type"] ?? "");
      if (contentType.includes("application/json")) {
        throw new Error("No data to export");
      }

      const validatedResponse = ExportRigFracActivityResponseSchema.parse(response);
      return validatedResponse as AxiosResponse<Blob>;
    },
    {
      onSuccess: onSuccess
        ? (response, params) => onSuccess(response, params)
        : undefined,
      onError: onError ? (error) => onError(error as Error) : undefined
    }
  );
}
